import history from '@gen2/app/components/router/history';
import { getUserToken } from '@gen2/utils/auth';
import { getCookie } from '@gen2/utils/cookies';
import * as Sentry from '@sentry/react';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { has, isNil, omit } from 'lodash';
import * as R from 'ramda';

interface IAxiosRequestConfig extends AxiosRequestConfig {
  omit?: Array<string>;
}

// Helper function to extract event ID from error message
const extractEventIdFromError = (error: AxiosError): string | null => {
  try {
    // Check if error message contains an event ID
    const errorData = error.response?.data as any;
    const errorMessage = errorData?.message || error.message || '';
    const eventIdMatch = errorMessage.match(/Event ID: ([a-f0-9-]+)/i);
    return eventIdMatch ? eventIdMatch[1] : null;
  } catch {
    return null;
  }
};

// Helper function to create a user-friendly error message without the event ID
const createUserFriendlyMessage = (error: AxiosError): string => {
  try {
    const errorData = error.response?.data as any;
    const errorMessage =
      errorData?.message || error.message || 'An unexpected error occurred';
    // Remove the event ID from the message for user display
    return errorMessage
      .replace(/\s*[-–—]?\s*Event ID:\s*[a-f0-9-]+/i, '')
      .trim();
  } catch {
    return 'An unexpected error occurred';
  }
};

class HttpRequest {
  baseUrl: string;
  queue: {
    [propName: string]: boolean;
  };

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.queue = {};
  }

  async getInsideConfig(hasCustomeAuthorization: boolean, isGlobal: boolean) {
    const accessToken = hasCustomeAuthorization ? '' : await getUserToken();
    const organisationId = sessionStorage.getItem('FI-ORGANISATION-ID');
    const accountId = sessionStorage.getItem('FI-ACCOUNT-ID');

    const baseUrl = isGlobal
      ? this.baseUrl
      : `//${localStorage.getItem('regionEndpoint')}`;

    const config: AxiosRequestConfig = {
      baseURL: baseUrl,
      headers: {
        Authorization: `Bearer ${accessToken}`, // jwt access_token
        'organisation-id': organisationId,
        'account-id': accountId,
      },
      withCredentials: true, // carry cookies except use mocking request env
    };

    return config;
  }

  destroy(url: string) {
    delete this.queue[url];
  }

  interceptors(instance: AxiosInstance, url: string) {
    instance.interceptors.request.use(
      (config: AxiosRequestConfig): InternalAxiosRequestConfig => {
        // if (!Object.keys(this.queue).length) {
        // do something before sending requests
        // }
        this.queue[url] = true;

        return config as InternalAxiosRequestConfig;
      },
      (error: AxiosError): Promise<AxiosError> => {
        return Promise.reject(error);
      },
    );
    instance.interceptors.response.use(
      (response: AxiosResponse): AxiosResponse => {
        this.destroy(url);
        // do something after getting responses

        return response;
      },
      (error: AxiosError): Promise<AxiosError> => {
        if (error.response) {
          if (
            error.response.status === 401 &&
            getCookie('accessToken') &&
            getCookie('refreshToken')
          ) {
            // Redirect to the logout page
            history.push('/logout');
          }

          if (error.response.status === 402) {
            history.push('/on-hold');
          }

          // Handle 500 errors with Sentry integration
          if (error.response.status >= 500 && error.response.status < 600) {
            const eventId = extractEventIdFromError(error);
            const userFriendlyMessage = createUserFriendlyMessage(error);

            // Create a custom error object for Sentry
            const sentryError = new Error(userFriendlyMessage);
            sentryError.name = `HTTP ${error.response.status} Error with API EVENT ID: ${eventId}`;

            // Capture the error with Sentry and get the event ID
            const sentryEventId = Sentry.captureException(sentryError, {
              tags: {
                error_type: 'http_500_error',
                status_code: '500',
                url: url,
              },
              contexts: {
                http: {
                  method: error.config?.method?.toUpperCase(),
                  url: error.config?.url,
                  status_code: error.response.status,
                },
                response: {
                  data: error.response.data,
                  headers: error.response.headers as Record<string, string>,
                },
              },
              extra: {
                originalError: error,
                apiEventId: eventId,
                originalMessage:
                  (error.response?.data as any)?.message || error.message,
              },
            });

            // Dispatch custom event for React components to listen to
            const customEvent = new CustomEvent('http-error-500', {
              detail: {
                error: sentryError,
                sentryEventId,
                apiEventId: eventId,
                originalError: error,
              },
            });
            window.dispatchEvent(customEvent);
          }
        }

        this.destroy(url);

        return Promise.reject(error.response);
      },
    );
  }

  async request(options: IAxiosRequestConfig) {
    const instance = axios.create();
    let pureOptions = R.mergeDeepRight(
      await this.getInsideConfig(
        has(options, 'headers.Authorization'),
        has(options, 'headers.global'),
      ),
      options,
    ) as AxiosRequestConfig;
    if (!isNil(options.omit)) {
      pureOptions = omit(pureOptions, options.omit);
      delete options.omit;
    }
    this.interceptors(instance, pureOptions.url || '__None__');
    return instance(pureOptions);
  }
}
export default HttpRequest;
